import { Box, Center, Flex, Button, Text, useToast, useDisclosure } from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useCallback, useState, useMemo } from 'react';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { SortOrder } from '@duke-hq/lib/pagination';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import {
  ProviderCauseStatusSchema,
  type ProviderCauseStatus,
} from '@duke-hq/svc-api-db/schemas/enums/ProviderCauseStatus.schema';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { CausesManagementList } from './CausesManagementList';
import { CausesFilterButtons } from './CausesFilterButtons';
import { CauseFormModal } from './CauseFormModal';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { Plus } from 'lucide-react';
import { AdminCreateCauseForm } from './AdminCreateCauseForm';

interface SortFilters {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  page: string | undefined;
  status: ProviderCauseStatus | undefined;
}

const DEFAULT_PAGE_SIZE = 5;

export function CausesManagement() {
  const [search, setSearch] = useState('');
  const toast = useToast();
  const utils = trpcTenant.useUtils();
  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();
  const [selectedCause, setSelectedCause] = useState<
    RouterOutputs['groupRewards']['listCausesWithInfiniteScroll']['items'][number] | null
  >(null);

  const [filters, setFilters] = useState<SortFilters>({
    orderBy: 'createdAt',
    sort: SortOrder.DESC,
    status: undefined,
    page: '1',
  });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    trpcTenant.groupRewards.listCausesWithInfiniteScroll.useInfiniteQuery(
      {
        search: search.trim(),
        filters,
        limit: DEFAULT_PAGE_SIZE,
      },
      {
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        placeholderData: (prev) => prev,
        staleTime: 5 * 60 * 1000,
      },
    );

  const causes = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  const approveCauseMutation = trpcTenant.groupRewards.approveCause.useMutation({
    onSuccess: (_data, variables) => {
      toast({
        title: 'Success',
        description: 'Cause approved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      if (selectedCause && selectedCause.id === variables.causeId) {
        setSelectedCause({
          ...selectedCause,
          status: ProviderCauseStatusSchema.enum.APPROVED,
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : t`Failed to approve cause. Please try again.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
    onSettled() {
      void utils.groupRewards.listCauses.invalidate();
      void utils.groupRewards.listCausesWithInfiniteScroll.invalidate();
    },
  });

  const archiveCauseMutation = trpcTenant.groupRewards.archiveCause.useMutation({
    onSuccess: (_data, variables) => {
      toast({
        title: 'Success',
        description: 'Cause archived successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      if (selectedCause && selectedCause.id === variables.causeId) {
        setSelectedCause({
          ...selectedCause,
          status: ProviderCauseStatusSchema.enum.ARCHIVED,
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : t`Failed to archive cause. Please try again.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
    onSettled() {
      void utils.groupRewards.listCauses.invalidate();
      void utils.groupRewards.listCausesWithInfiniteScroll.invalidate();
    },
  });

  const handleApproveCause = useCallback(
    (causeId: string) => {
      approveCauseMutation.mutate({ causeId });
    },
    [approveCauseMutation],
  );

  const handleArchiveCause = useCallback(
    (causeId: string) => {
      archiveCauseMutation.mutate({ causeId });
    },
    [archiveCauseMutation],
  );

  const handleEditCause = useCallback(
    (causeId: string) => {
      const cause = causes?.find((c) => c.id === causeId);
      if (cause) {
        setSelectedCause(cause);
        onEditModalOpen();
      }
    },
    [causes, onEditModalOpen],
  );

  const handleEditModalClose = useCallback(() => {
    setSelectedCause(null);
    onEditModalClose();
  }, [onEditModalClose]);

  const handleFilterChange = useCallback((newFilters: Partial<SortFilters>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  }, []);

  const handleLoadMore = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  const approvingCauseId =
    approveCauseMutation.isPending && approveCauseMutation.variables?.causeId
      ? approveCauseMutation.variables.causeId
      : undefined;

  const archivingCauseId =
    archiveCauseMutation.isPending && archiveCauseMutation.variables?.causeId
      ? archiveCauseMutation.variables.causeId
      : undefined;

  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <>
      <TableViewLayout
        tableHeight="auto"
        tableBgColor="bg.subtle"
        pageActions={[
          <FormModalWrapper
            key="create-cause-modal"
            modalOpenButtonText={t`Create a cause`}
            modalOpenButtonLeftIcon={Plus}
            modalContent={<AdminCreateCauseForm onClose={onClose} />}
            isOpen={isOpen}
            onClose={onClose}
            onOpen={onOpen}
            modalContentHeight="auto"
            modalCloseButton={false}
          />,
        ]}
        pageHeader={[
          <PageHeading key="page-heading">
            <Trans>Causes</Trans>
          </PageHeading>,
        ]}
        filters={
          <Flex justify="space-between" align="center" width="100%" flexWrap="wrap" gap={2}>
            <SearchInput onCommit={setSearch} placeholder={t`Search by name`} maxW="search-input" />
            <CausesFilterButtons filters={filters} onFilterChange={handleFilterChange} />
          </Flex>
        }
        table={
          Array.isArray(causes) && causes.length > 0 ? (
            <Box maxHeight="600px" overflowY="auto" borderRadius="md">
              <CausesManagementList
                causes={causes}
                onApproveCause={handleApproveCause}
                onArchiveCause={handleArchiveCause}
                onEditCause={handleEditCause}
                approvingCauseId={approvingCauseId}
                archivingCauseId={archivingCauseId}
              />
            </Box>
          ) : (
            <Center p={8}>
              <Text textAlign="center">
                <Trans>No causes found</Trans>
              </Text>
            </Center>
          )
        }
      />

      {hasNextPage ? (
        <Flex justify="center" my={4}>
          <Button
            onClick={handleLoadMore}
            isLoading={isFetchingNextPage}
            loadingText="Loading more..."
            variant="link"
            size="xs"
            colorScheme="teal"
          >
            <Trans>Load More ({causes.length} loaded)</Trans>
          </Button>
        </Flex>
      ) : null}

      {selectedCause != null ? (
        <CauseFormModal
          editCause={selectedCause}
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
          onApproveCause={handleApproveCause}
          onArchiveCause={handleArchiveCause}
          approvingCauseId={approvingCauseId}
          archivingCauseId={archivingCauseId}
        />
      ) : null}
    </>
  );
}
